/*
  BreakingT Custom Lander engine
  --------------------------------
  Shared JS for pages using templates/page.custom-lander.liquid.
  Reads:
    #btLanderData    – JSON rendered by the template (hero product, recs, assets, endpoints)
    #btLanderConfig  – optional JSON pasted in the page's lander_code (overrides anything in btLanderData)
    #btLanderProduct – hero product JSON rendered by the template from the lander_product metafield
  Wires up any of these element IDs that exist on the page (all optional):
    #btBuy #btHeroOptions #btHeroPrice #btHeroPriceNote #btMainImage #btThumbs #btZoomOpen #btModalImage
    #btQtyMinus #btQtyOutput #btQtyPlus #btAddToCart #btAddToCartText #btAvailability
    #btMobileBar #btMobilePrice #btMobileSelection #btMobileAction
    #btRecRail #btRecPrev #btRecNext #btRecCurrent #btRecTotal #btMore
    #btHeaderCart #btHeaderCartCount #btCartOverlay #btCartDrawer #btCartBody #btCartClose
    #btQuickOverlay #btQuickShop #btQuickBody #btQuickClose #btQuickTitle
    #btSizeGuideOpen #btSizeModal #btSizeTabs #btSizeTable #btImageModal #btToast
  Data attributes: [data-quick-shop="handle"] [data-scroll-buy] [data-cart-more] [data-cart-shop]
                   [data-close-modal="id"] [data-bt-asset="filename"] [data-bt-open-modal="id"]
  Offer mode (page.offer-lander): when #btLanderData carries an "offer" object, also wires
    #btOfferCards [data-offer-card] [data-card-form] [data-card-image] [data-card-price]
    #btOfferTracker #btOfferCount #btOfferFill #btOfferMessage #btOfferCta
    [data-copy-code] [data-jump-card="handle"] [data-card-add="handle"]
  Exposes window.BTLander for page-specific scripts.
*/
(() => {
  'use strict';

  const $ = (selector, root = document) => root.querySelector(selector);
  const $$ = (selector, root = document) => [...root.querySelectorAll(selector)];
  const parseJSON = id => {
    const node = document.getElementById(id);
    if (!node) return null;
    try { return JSON.parse(node.textContent); } catch (error) { console.warn('[bt-lander] Could not parse #' + id, error); return null; }
  };

  const DATA = parseJSON('btLanderData') || {};
  const PAGE = parseJSON('btLanderConfig') || {};
  const CONFIG = { ...DATA, ...PAGE };

  const COPY = {
    recLabel: '',
    listName: 'Recommended products',
    cartTitle: 'Your BreakingT cart',
    cartEmptyTitle: 'Your cart is empty.',
    cartEmptyCopy: 'Add a shirt to get started.',
    cartEmptyButton: 'Shop the shirt',
    keepShopping: 'Keep shopping',
    checkout: 'Checkout securely',
    quickAddLabel: 'Quick add',
    ...(CONFIG.copy || {})
  };

  let heroProduct = parseJSON('btLanderProduct') || CONFIG.heroProduct || null;

  const state = {
    hero: null,
    quantity: 1,
    recs: Array.isArray(CONFIG.fallbackProducts) ? CONFIG.fallbackProducts.filter(p => p && p.id) : [],
    quick: null,
    cart: null,
    loadingHero: false,
    loadingQuick: false,
    recIndex: 0,
    returnFocus: null
  };

  const DEFAULT_CHARTS = {
    'Adult T-Shirt': { sizes: ['S','M','L','XL','2XL','3XL'], rows: { Length: ['28','29','30','31','32','33'], Width: ['19','20.5','22','24','26','28'] } },
    'Hoodie': { sizes: ['S','M','L','XL','2XL','3XL'], rows: { Length: ['27','28','29','29.75','30.25','30.75'], Width: ['20','21.5','23','25','27','29'] } },
    "Women's V-Neck": { sizes: ['S','M','L','XL','2XL','3XL'], rows: { Length: ['25','25.75','26.5','27.25','28','28.75'], Width: ['18.5','20','22','24','26','28'] } },
    'Youth T-Shirt': { sizes: ['S (6–7)','M (8)','L (10/12)','XL (14/16)'], rows: { Length: ['20.5','22','23.5','25'], Width: ['15.25','16.75','18.25','19.75'] } },
    'Youth Hoodie': { sizes: ['S (6–8)','M (10–12)','L (14–16)','XL (18–20)'], rows: { Length: ['21.25','22.5','24','25.5'], Width: ['17','18','19','20'] } }
  };
  const charts = CONFIG.sizeCharts && typeof CONFIG.sizeCharts === 'object' ? CONFIG.sizeCharts : DEFAULT_CHARTS;

  const els = {
    buy: $('#btBuy'),
    heroOptions: $('#btHeroOptions'),
    heroPrice: $('#btHeroPrice'),
    heroPriceNote: $('#btHeroPriceNote'),
    mainImage: $('#btMainImage'),
    thumbs: $('#btThumbs'),
    modalImage: $('#btModalImage'),
    add: $('#btAddToCart'),
    addText: $('#btAddToCartText'),
    availability: $('#btAvailability'),
    qtyOutput: $('#btQtyOutput'),
    mobileBar: $('#btMobileBar'),
    mobilePrice: $('#btMobilePrice'),
    mobileSelection: $('#btMobileSelection'),
    mobileAction: $('#btMobileAction'),
    recRail: $('#btRecRail'),
    recCurrent: $('#btRecCurrent'),
    recTotal: $('#btRecTotal'),
    more: $('#btMore'),
    cartOverlay: $('#btCartOverlay'),
    cartDrawer: $('#btCartDrawer'),
    cartBody: $('#btCartBody'),
    cartClose: $('#btCartClose'),
    headerCart: $('#btHeaderCart'),
    headerCartCount: $('#btHeaderCartCount'),
    quickOverlay: $('#btQuickOverlay'),
    quickShop: $('#btQuickShop'),
    quickBody: $('#btQuickBody'),
    quickClose: $('#btQuickClose'),
    quickTitle: $('#btQuickTitle'),
    toast: $('#btToast'),
    sizeModal: $('#btSizeModal'),
    sizeTabs: $('#btSizeTabs'),
    sizeTable: $('#btSizeTable'),
    imageModal: $('#btImageModal')
  };

  /* ---------- helpers ---------- */
  function money(cents) {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: CONFIG.currency || 'USD' }).format((Number(cents) || 0) / 100);
  }
  function escapeHtml(value) {
    return String(value ?? '')
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#039;');
  }
  function escapeAttr(value) { return escapeHtml(value).replace(/`/g, '&#096;'); }
  function imageSource(image) { return typeof image === 'string' ? image : image?.src || image?.url || image?.original_src || ''; }
  function readable(value) {
    return String(value || '').replace(/^SMALL$/i,'S').replace(/^MEDIUM$/i,'M').replace(/^LARGE$/i,'L').replace(/^EXTRA LARGE$/i,'XL');
  }
  function optionNames(product) { return (product?.options || []).map(option => typeof option === 'string' ? option : option.name); }
  function optionIndex(product, regex) { return optionNames(product).findIndex(name => regex.test(String(name || ''))); }
  function variantValue(variant, index) { return Array.isArray(variant?.options) ? variant.options[index] : variant?.[`option${index + 1}`]; }
  function optionValues(product, index) { return [...new Set((product?.variants || []).map(v => variantValue(v,index)).filter(Boolean))]; }
  function firstAvailable(product) { return (product?.variants || []).find(v => v.available !== false) || product?.variants?.[0] || null; }
  function productImages(product) {
    const list = Array.isArray(product?.images) ? product.images.map(imageSource).filter(Boolean) : [];
    const featured = imageSource(product?.featured_image);
    if (featured && !list.includes(featured)) list.unshift(featured);
    return list;
  }

  /* ---------- variant selection ---------- */
  function makeSelection(product) {
    const first = firstAvailable(product);
    const sizeIndex = optionIndex(product, /size/i);
    const selected = {};
    optionNames(product).forEach((_, index) => { selected[index] = index === sizeIndex ? null : variantValue(first,index); });
    return { selected };
  }

  function matches(product, selected, overrides = {}, ignoreSize = false) {
    const sizeIndex = optionIndex(product, /size/i);
    return (product?.variants || []).filter(variant => optionNames(product).every((_, index) => {
      if (ignoreSize && index === sizeIndex) return true;
      const expected = Object.prototype.hasOwnProperty.call(overrides,index) ? overrides[index] : selected[index];
      return expected == null || expected === '' || variantValue(variant,index) === expected;
    }));
  }

  function chosenVariant(product, selection) {
    if (!product || !selection) return null;
    const candidates = matches(product, selection.selected);
    return candidates.find(v => v.available !== false) || candidates[0] || null;
  }

  function displayVariant(product, selection) {
    const sizeIndex = optionIndex(product, /size/i);
    const chosen = { ...selection.selected };
    if (sizeIndex >= 0) chosen[sizeIndex] = null;
    const candidates = matches(product, chosen);
    return candidates.find(v => v.available !== false) || candidates[0] || firstAvailable(product);
  }

  function currentPrice(product, selection) {
    return Number((chosenVariant(product,selection) || displayVariant(product,selection))?.price || product?.price_min || product?.price || 0);
  }

  function styleValue(product, selection) {
    const styleIndex = optionIndex(product, /style/i);
    return styleIndex >= 0 ? selection.selected[styleIndex] : 'Adult T-Shirt';
  }

  function hasSize(product, selection) {
    const sizeIndex = optionIndex(product,/size/i);
    return sizeIndex < 0 || Boolean(selection.selected[sizeIndex]);
  }

  function normalizeAfterChange(product, selection, changedIndex) {
    const sizeIndex = optionIndex(product,/size/i);
    if (changedIndex !== sizeIndex && sizeIndex >= 0) selection.selected[sizeIndex] = null;
    if (changedIndex === sizeIndex) return;

    let candidates = matches(product, selection.selected, {}, true).filter(v => v.available !== false);
    if (!candidates.length) {
      const changedValue = selection.selected[changedIndex];
      candidates = (product.variants || []).filter(v => v.available !== false && variantValue(v,changedIndex) === changedValue);
    }
    const fallback = candidates[0] || firstAvailable(product);
    if (!fallback) return;

    optionNames(product).forEach((_, index) => {
      if (index === sizeIndex || index === changedIndex) return;
      const exists = candidates.some(v => variantValue(v,index) === selection.selected[index]);
      if (!exists) selection.selected[index] = variantValue(fallback,index);
    });
  }

  function optionGroupHtml(product, selection, index, scope) {
    const name = optionNames(product)[index] || `Option ${index + 1}`;
    const values = optionValues(product,index);
    const sizeIndex = optionIndex(product,/size/i);
    const selected = selection.selected[index];
    const isColor = /color/i.test(name);
    // scope: 'hero' | 'quick' | 'card:<handle>'
    const card = scope.startsWith('card:') ? scope.slice(5) : null;
    const dataPrefix = card ? 'data-card' : scope === 'quick' ? 'data-quick' : 'data-hero';
    const cardAttr = card ? ` data-card="${escapeAttr(card)}"` : '';

    if (isColor && values.length > 5) {
      return `<div class="bt-option-group" data-option-group="${index}"><div class="bt-option-head"><strong>${escapeHtml(name)}</strong><span>${escapeHtml(selected || '')}</span></div><select class="bt-option-select" ${dataPrefix}-select="${index}"${cardAttr} aria-label="Choose ${escapeAttr(name)}">${values.map(v => `<option value="${escapeAttr(v)}"${v === selected ? ' selected' : ''}>${escapeHtml(v)}</option>`).join('')}</select></div>`;
    }

    const buttons = values.map(value => {
      const ignoreSize = index !== sizeIndex;
      const candidates = matches(product, selection.selected, { [index]: value }, ignoreSize);
      const available = candidates.some(v => v.available !== false);
      return `<button class="bt-option-button" type="button" ${dataPrefix}-option="${index}"${cardAttr} data-value="${escapeAttr(value)}" aria-pressed="${selected === value}"${available ? '' : ' disabled'}>${escapeHtml(readable(value))}</button>`;
    }).join('');

    const label = selected ? readable(selected) : index === sizeIndex ? 'Choose one' : '';
    return `<div class="bt-option-group" data-option-group="${index}"><div class="bt-option-head"><strong>${escapeHtml(name)}</strong><span>${escapeHtml(label)}</span></div><div class="bt-option-values" aria-label="Choose ${escapeAttr(name)}">${buttons}</div></div>`;
  }

  /* ---------- gallery ---------- */
  function setMainImage(src, alt) {
    if (!src) return;
    if (els.mainImage) { els.mainImage.src = src; if (alt != null) els.mainImage.alt = alt; }
    if (els.modalImage) { els.modalImage.src = src; if (alt != null) els.modalImage.alt = alt; }
    $$('.bt-thumb').forEach(button => {
      const active = button.dataset.gallerySrc === src;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', String(active));
    });
  }

  function buildGallery() {
    if (!heroProduct) return;
    const images = productImages(heroProduct);
    if (!images.length) return;
    if (els.mainImage && !els.mainImage.getAttribute('src')) {
      els.mainImage.src = images[0];
      if (!els.mainImage.alt) els.mainImage.alt = heroProduct.title || '';
    }
    if (els.modalImage && !els.modalImage.getAttribute('src')) {
      els.modalImage.src = images[0];
      if (!els.modalImage.alt) els.modalImage.alt = heroProduct.title || '';
    }
    if (els.thumbs && !els.thumbs.children.length && images.length > 1) {
      els.thumbs.innerHTML = images.slice(0, Number(CONFIG.maxThumbs) || 6).map((src, i) => `<button type="button" class="bt-thumb${i === 0 ? ' is-active' : ''}" data-gallery-src="${escapeAttr(src)}" data-gallery-alt="${escapeAttr(heroProduct.title || '')}" aria-label="View product image ${i + 1}" aria-pressed="${i === 0}"><img src="${escapeAttr(src.replace(/width=\d+/, 'width=180'))}" alt="" width="90" height="90" loading="${i < 3 ? 'eager' : 'lazy'}"></button>`).join('');
    }
  }

  /* ---------- hero ---------- */
  function heroRender() {
    if (!heroProduct || !state.hero) return;
    if (els.heroOptions) els.heroOptions.innerHTML = optionNames(heroProduct).map((_,i) => optionGroupHtml(heroProduct,state.hero,i,'hero')).join('');

    const variant = chosenVariant(heroProduct,state.hero);
    const visualVariant = variant || displayVariant(heroProduct,state.hero);
    const price = currentPrice(heroProduct,state.hero);
    const sizeReady = hasSize(heroProduct,state.hero);
    const available = Boolean(variant?.id && variant.available !== false);
    const style = styleValue(heroProduct,state.hero) || 'Selected style';
    const sizeIndex = optionIndex(heroProduct,/size/i);
    const size = sizeIndex >= 0 ? state.hero.selected[sizeIndex] : '';
    const image = imageSource(visualVariant?.featured_image);

    if (els.heroPrice) els.heroPrice.textContent = money(price);
    if (els.heroPriceNote) els.heroPriceNote.textContent = style;
    if (els.mobilePrice) els.mobilePrice.textContent = money(price);
    if (els.mobileSelection) els.mobileSelection.textContent = sizeReady ? `${style}${size ? ` · ${readable(size)}` : ''}` : 'Choose a size';
    if (image && CONFIG.swapImageOnVariant !== false) setMainImage(image);

    if (els.add) els.add.disabled = state.loadingHero || !sizeReady || !available;
    if (els.addText) els.addText.textContent = state.loadingHero ? 'Adding…' : !sizeReady ? 'Choose a size' : !available ? 'Selection sold out' : `Add to cart · ${money(price)}`;
    if (els.mobileAction) { els.mobileAction.textContent = !sizeReady ? 'Choose size' : state.loadingHero ? 'Adding…' : 'Add to cart'; els.mobileAction.disabled = state.loadingHero; }
    if (els.availability) {
      els.availability.textContent = !sizeReady ? 'Choose a size to continue.' : !available ? 'That combination is sold out. Try another option.' : 'In stock and ready to order.';
      els.availability.classList.toggle('is-error', sizeReady && !available);
    }
    document.dispatchEvent(new CustomEvent('bt-lander:hero-render', { detail: { product: heroProduct, variant, price, sizeReady, available } }));
  }

  function selectHero(index, value) {
    if (!state.hero) return;
    state.hero.selected[index] = value;
    normalizeAfterChange(heroProduct,state.hero,index);
    heroRender();
  }

  /* ---------- quick shop ---------- */
  function quickRender() {
    if (!state.quick || !els.quickBody) return;
    const { product, selection } = state.quick;
    const variant = chosenVariant(product,selection);
    const visual = variant || displayVariant(product,selection);
    const sizeReady = hasSize(product,selection);
    const available = Boolean(variant?.id && variant.available !== false);
    const price = currentPrice(product,selection);
    const image = imageSource(visual?.featured_image) || imageSource(product.featured_image);
    const options = optionNames(product).map((_,i) => optionGroupHtml(product,selection,i,'quick')).join('');
    if (els.quickTitle) els.quickTitle.textContent = product.title;
    els.quickBody.innerHTML = `<div class="bt-quick-product"><div class="bt-quick-product__top"><img src="${escapeAttr(image)}" alt="${escapeAttr(product.title)}" width="240" height="240"><div><span>${escapeHtml(COPY.recLabel)}</span><h3>${escapeHtml(product.title)}</h3><strong>${money(price)}</strong></div></div><div class="bt-options">${options}</div><button class="bt-quick-add" id="btQuickAdd" type="button"${state.loadingQuick || !sizeReady || !available ? ' disabled' : ''}>${state.loadingQuick ? 'Adding…' : !sizeReady ? 'Choose a size' : !available ? 'Selection sold out' : `Add to cart · ${money(price)}`}</button></div>`;
  }

  function selectQuick(index,value) {
    if (!state.quick) return;
    state.quick.selection.selected[index] = value;
    normalizeAfterChange(state.quick.product,state.quick.selection,index);
    quickRender();
  }

  /* ---------- layers ---------- */
  function showToast(message) {
    if (!els.toast) return;
    els.toast.textContent = message;
    els.toast.classList.add('show');
    clearTimeout(showToast.timer);
    showToast.timer = setTimeout(() => els.toast.classList.remove('show'), 3000);
  }

  function lockPage(lock) { document.body.classList.toggle('bt-lock', Boolean(lock)); }

  function openLayer(layer, overlay, focusEl) {
    if (!layer) return;
    state.returnFocus = document.activeElement;
    if (overlay) overlay.hidden = false;
    layer.hidden = false;
    lockPage(true);
    requestAnimationFrame(() => focusEl?.focus());
  }

  function closeLayer(layer, overlay) {
    if (!layer) return;
    layer.hidden = true;
    if (overlay) overlay.hidden = true;
    lockPage(false);
    state.returnFocus?.focus?.();
    state.returnFocus = null;
  }

  /* ---------- cart ---------- */
  async function addVariant(variantId, quantity = 1, properties) {
    const item = { id: variantId, quantity };
    if (properties && typeof properties === 'object') item.properties = properties;
    const response = await fetch('/cart/add.js', {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: [item] })
    });
    if (!response.ok) throw new Error('add_failed');
    return response.json();
  }

  function track(event,payload={}) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event, ecommerce: payload });
  }

  async function addHero() {
    if (!heroProduct || !state.hero || state.loadingHero) return;
    const sizeIndex = optionIndex(heroProduct,/size/i);
    if (sizeIndex >= 0 && !state.hero.selected[sizeIndex]) {
      document.querySelector(`[data-option-group="${sizeIndex}"]`)?.scrollIntoView({ behavior:'smooth', block:'center' });
      showToast('Choose a size first.');
      return;
    }
    const variant = chosenVariant(heroProduct,state.hero);
    if (!variant?.id || variant.available === false) { showToast('Choose an available option.'); return; }
    state.loadingHero = true; heroRender();
    try {
      await addVariant(variant.id,state.quantity);
      track('add_to_cart',{ currency:'USD', value:Number(variant.price || 0) * state.quantity / 100, items:[{ item_id:String(variant.id), item_name:heroProduct.title, item_variant:variant.title, price:Number(variant.price || 0)/100, quantity:state.quantity }] });
      document.dispatchEvent(new CustomEvent('bt-lander:added', { detail: { product: heroProduct, variant, quantity: state.quantity, source: 'hero' } }));
      if (CONFIG.directCheckout) { location.href = CONFIG.checkoutUrl || '/checkout'; return; }
      await fetchCart();
      openCart();
    } catch { showToast('That item could not be added. Please try again.'); }
    finally { state.loadingHero = false; heroRender(); }
  }

  async function addQuick() {
    if (!state.quick || state.loadingQuick) return;
    const variant = chosenVariant(state.quick.product,state.quick.selection);
    if (!variant?.id || variant.available === false || !hasSize(state.quick.product,state.quick.selection)) return;
    state.loadingQuick = true; quickRender();
    try {
      await addVariant(variant.id,1);
      track('add_to_cart',{ currency:'USD', value:Number(variant.price || 0)/100, items:[{ item_id:String(variant.id), item_name:state.quick.product.title, item_variant:variant.title, price:Number(variant.price || 0)/100, quantity:1 }] });
      document.dispatchEvent(new CustomEvent('bt-lander:added', { detail: { product: state.quick.product, variant, quantity: 1, source: 'quick' } }));
      closeQuick();
      await fetchCart();
      openCart();
    } catch { showToast('That item could not be added. Please try again.'); }
    finally { state.loadingQuick = false; quickRender(); }
  }

  async function fetchCart() {
    const response = await fetch('/cart.js',{ credentials:'include', headers:{ Accept:'application/json' } });
    if (!response.ok) throw new Error('cart_failed');
    state.cart = await response.json();
    updateCartCount();
    if (els.cartDrawer && !els.cartDrawer.hidden) renderCart();
    document.dispatchEvent(new CustomEvent('bt-lander:cart', { detail: { cart: state.cart } }));
    return state.cart;
  }

  function updateCartCount() {
    const count = Number(state.cart?.item_count || 0);
    if (!els.headerCartCount) return;
    els.headerCartCount.hidden = count === 0;
    els.headerCartCount.textContent = `(${count})`;
  }

  function cartItemHtml(item,index) {
    const image = imageSource(item.image) || imageSource(item.featured_image);
    return `<article class="bt-cart-item"><img src="${escapeAttr(image)}" alt="" width="144" height="144"><div><h3>${escapeHtml(item.product_title || item.title)}</h3><p>${escapeHtml(item.variant_title && item.variant_title !== 'Default Title' ? item.variant_title : '')}</p><div class="bt-cart-item__bottom"><div class="bt-cart-qty"><button type="button" data-cart-line="${index + 1}" data-cart-qty="${Math.max(0,Number(item.quantity)-1)}" aria-label="Decrease quantity">−</button><span>${Number(item.quantity)}</span><button type="button" data-cart-line="${index + 1}" data-cart-qty="${Number(item.quantity)+1}" aria-label="Increase quantity">+</button></div><strong>${money(item.final_line_price ?? item.line_price)}</strong></div><button class="bt-cart-remove" type="button" data-cart-line="${index + 1}" data-cart-qty="0">Remove</button></div></article>`;
  }

  function renderCart() {
    if (!els.cartBody || !state.cart) return;
    if (!state.cart.items?.length) {
      els.cartBody.innerHTML = `<div class="bt-cart-empty"><h3>${escapeHtml(COPY.cartEmptyTitle)}</h3><p>${escapeHtml(COPY.cartEmptyCopy)}</p><button class="bt-primary" type="button" data-cart-shop>${escapeHtml(COPY.cartEmptyButton)}</button></div>`;
      return;
    }
    const threshold = Number(CONFIG.shippingThreshold || 0);
    const total = Number(state.cart.total_price || 0);
    let shipping = '';
    if (threshold > 0) {
      const remaining = Math.max(0,threshold-total);
      const progress = Math.min(100,total/threshold*100);
      const shippingCopy = remaining > 0 ? `${money(remaining)} away from free shipping.` : 'Free shipping unlocked.';
      shipping = `<div class="bt-shipping-progress"><p>${escapeHtml(shippingCopy)}</p><div class="bt-shipping-progress__track"><span style="width:${progress}%"></span></div></div>`;
    }
    const checkoutHref = CONFIG.checkoutUrl || '/checkout';
    let offerProgress = '';
    let checkoutCta = `<a href="${escapeAttr(checkoutHref)}">${escapeHtml(COPY.checkout)}</a>`;
    let keepLabel = COPY.keepShopping;
    if (OFFER) {
      const summary = offerCartSummary();
      offerProgress = summary.progress;
      checkoutCta = summary.cta;
      if (!summary.status.unlocked) keepLabel = summary.status.remaining === 1 ? 'Add one more qualifying shirt' : COPY.keepShopping;
    }
    els.cartBody.innerHTML = `${state.cart.items.map(cartItemHtml).join('')}<div class="bt-cart-summary">${offerProgress}<div class="bt-cart-summary__row"><span>Subtotal</span><strong>${money(total)}</strong></div>${shipping}<div class="bt-cart-actions">${checkoutCta}<button type="button" data-cart-more>${escapeHtml(keepLabel)}</button></div></div>`;
  }

  async function changeCart(line,quantity) {
    try {
      const response = await fetch('/cart/change.js',{ method:'POST', credentials:'include', headers:{ 'Content-Type':'application/json','Accept':'application/json' }, body:JSON.stringify({ line:Number(line), quantity:Number(quantity) }) });
      if (!response.ok) throw new Error('change_failed');
      state.cart = await response.json();
      updateCartCount(); renderCart();
      document.dispatchEvent(new CustomEvent('bt-lander:cart', { detail: { cart: state.cart } }));
    } catch { showToast('Cart update failed. Please try again.'); }
  }

  async function openCart() {
    if (!els.cartDrawer) { location.href = '/cart'; return; }
    if (!state.cart) { try { await fetchCart(); } catch {} }
    renderCart();
    openLayer(els.cartDrawer,els.cartOverlay,els.cartClose);
    els.headerCart?.setAttribute('aria-expanded','true');
  }

  function closeCart() {
    closeLayer(els.cartDrawer,els.cartOverlay);
    els.headerCart?.setAttribute('aria-expanded','false');
  }

  function openQuick(product) {
    if (!product || !els.quickShop) return;
    state.quick = { product, selection:makeSelection(product) };
    quickRender();
    openLayer(els.quickShop,els.quickOverlay,els.quickClose);
  }
  function closeQuick() { closeLayer(els.quickShop,els.quickOverlay); }

  /* ---------- recommendations ---------- */
  function recCard(product,index) {
    const image = imageSource(product.featured_image) || imageSource(firstAvailable(product)?.featured_image);
    return `<article class="bt-rec-card" data-rec-index="${index}"><a class="bt-rec-card__image" href="${escapeAttr(product.url || `/products/${product.handle}`)}" tabindex="-1" aria-hidden="true"><img src="${escapeAttr(image)}" alt="" width="700" height="700" loading="lazy"></a><div class="bt-rec-card__body"><span>${escapeHtml(COPY.recLabel)}</span><h3>${escapeHtml(product.title)}</h3><div><strong>From ${money(product.price_min || product.price || 0)}</strong><button type="button" data-quick-shop="${escapeAttr(product.handle)}">${escapeHtml(COPY.quickAddLabel)}</button></div></div></article>`;
  }

  function renderRecs() {
    if (!els.recRail || !state.recs.length) return;
    const limit = Number(CONFIG.maxRecs) || 8;
    els.recRail.innerHTML = state.recs.slice(0,limit).map(recCard).join('');
    if (els.recTotal) els.recTotal.textContent = String(Math.min(limit,state.recs.length));
    state.recIndex = 0;
    updateRecIndex();
  }

  function recEndpoint() {
    if (CONFIG.recommendationEndpoint) return CONFIG.recommendationEndpoint;
    if (CONFIG.recsCollection) return `/collections/${CONFIG.recsCollection}?sort_by=${CONFIG.recsSort || 'best-selling'}&view=bt-lander-recs`;
    return null;
  }

  async function loadRecs() {
    const endpoint = recEndpoint();
    if (!endpoint) { if (state.recs.length) renderRecs(); return; }
    try {
      const response = await fetch(endpoint,{ credentials:'include', headers:{ Accept:'application/json' } });
      if (!response.ok) throw new Error('rec_failed');
      const payload = await response.json();
      const exclude = new Set([CONFIG.productHandle, heroProduct?.handle].filter(Boolean));
      const products = Array.isArray(payload.products) ? payload.products.filter(p => p?.id && !exclude.has(p.handle)) : [];
      if (products.length) { state.recs = products; renderRecs(); }
      else if (state.recs.length) renderRecs();
      track('view_item_list',{ item_list_name:COPY.listName, items:state.recs.slice(0,8).map((p,i)=>({item_id:String(p.id),item_name:p.title,index:i+1,price:Number(p.price_min || p.price || 0)/100})) });
    } catch {
      if (!els.recRail?.children.length && state.recs.length) renderRecs();
    }
  }

  function productByHandle(handle) { return state.recs.find(p => p.handle === handle) || CONFIG.fallbackProducts?.find?.(p => p?.handle === handle) || (heroProduct?.handle === handle ? heroProduct : null); }

  async function fetchProduct(handle) {
    const known = productByHandle(handle);
    if (known) return known;
    const response = await fetch(`/products/${handle}.js`, { credentials:'include', headers:{ Accept:'application/json' } });
    if (!response.ok) throw new Error('product_failed');
    const product = await response.json();
    state.recs.push(product);
    return product;
  }

  function recMetrics() {
    const card = els.recRail?.querySelector('.bt-rec-card');
    if (!card) return { step:0,max:0 };
    const gap = parseFloat(getComputedStyle(els.recRail).gap || 0);
    return { step:card.getBoundingClientRect().width + gap, max:Math.max(0,els.recRail.scrollWidth-els.recRail.clientWidth) };
  }

  function updateRecIndex() {
    if (!els.recRail) return;
    const { step } = recMetrics();
    if (!step) return;
    state.recIndex = Math.max(0,Math.min(state.recs.length-1,Math.round(els.recRail.scrollLeft/step)));
    if (els.recCurrent) els.recCurrent.textContent = String(state.recIndex + 1);
  }

  function moveRecs(direction) {
    const { step } = recMetrics();
    if (!step) return;
    els.recRail.scrollBy({ left:direction*step, behavior:'smooth' });
  }

  function enableDragScroll(el) {
    if (!el) return;
    let down=false,startX=0,startScroll=0,moved=false;
    el.addEventListener('pointerdown',event => {
      if (event.pointerType === 'touch' || event.target.closest('button')) return;
      down=true;moved=false;startX=event.clientX;startScroll=el.scrollLeft;el.setPointerCapture?.(event.pointerId);el.classList.add('is-dragging');
    });
    el.addEventListener('pointermove',event => { if (!down) return; const delta=event.clientX-startX; if (Math.abs(delta)>4) moved=true; el.scrollLeft=startScroll-delta; });
    const stop=event=>{ if(!down)return; down=false; el.releasePointerCapture?.(event.pointerId); el.classList.remove('is-dragging'); setTimeout(updateRecIndex,0); };
    el.addEventListener('pointerup',stop);el.addEventListener('pointercancel',stop);
    el.addEventListener('click',event=>{ if(moved){event.preventDefault();event.stopPropagation();moved=false;} },true);
  }

  /* ---------- size guide / modals ---------- */
  function renderSizeGuide(style) {
    if (!els.sizeTabs || !els.sizeTable) return;
    const names = Object.keys(charts);
    if (!names.length) return;
    const chosen = charts[style] ? style : names[0];
    els.sizeTabs.innerHTML = names.map(name => `<button type="button" data-size-chart="${escapeAttr(name)}" aria-pressed="${name===chosen}">${escapeHtml(name)}</button>`).join('');
    const chart = charts[chosen];
    const head = `<tr><th>Measurement</th>${chart.sizes.map(size=>`<th>${escapeHtml(size)}</th>`).join('')}</tr>`;
    const body = Object.entries(chart.rows).map(([label,values]) => `<tr><td>${escapeHtml(label)}</td>${values.map(v=>`<td>${escapeHtml(v)}</td>`).join('')}</tr>`).join('');
    els.sizeTable.innerHTML = `<table><thead>${head}</thead><tbody>${body}</tbody></table>`;
  }

  function openSizeGuide() {
    if (!els.sizeModal) return;
    renderSizeGuide(heroProduct && state.hero ? styleValue(heroProduct,state.hero) : 'Adult T-Shirt');
    openModal(els.sizeModal);
  }
  function openModal(modal) { if (!modal) return; state.returnFocus = document.activeElement; modal.hidden = false; lockPage(true); modal.querySelector('button')?.focus(); }
  function closeModal(modal) { if (!modal) return; modal.hidden = true; lockPage(false); state.returnFocus?.focus?.(); state.returnFocus = null; }

  function setupMobileBar() {
    const card = $('.bt-purchase-card') || els.buy;
    if (!card || !els.mobileBar) return;
    const observer = new IntersectionObserver(entries => {
      const visible = entries[0]?.isIntersecting;
      els.mobileBar.hidden = Boolean(visible);
    },{ threshold:.18 });
    observer.observe(card);
  }

  function trapEscape(event) {
    if (event.key !== 'Escape') return;
    const openModalEl = $$('.bt-modal').find(m => !m.hidden);
    if (openModalEl) closeModal(openModalEl);
    else if (els.quickShop && !els.quickShop.hidden) closeQuick();
    else if (els.cartDrawer && !els.cartDrawer.hidden) closeCart();
  }

  function resolveAssets() {
    const assets = CONFIG.assets || {};
    $$('[data-bt-asset]').forEach(node => {
      const url = assets[node.dataset.btAsset];
      if (!url) return;
      if (node.tagName === 'IMG' || node.tagName === 'SOURCE') node.setAttribute(node.tagName === 'IMG' ? 'src' : 'srcset', url);
      else node.style.backgroundImage = `url("${url}")`;
    });
  }

  /* ---------- offer mode (page.offer-lander) ---------- */
  const OFFER = CONFIG.offer && typeof CONFIG.offer === 'object' ? CONFIG.offer : null;
  const offerEls = OFFER ? {
    cards: $('#btOfferCards'),
    tracker: $('#btOfferTracker'),
    count: $('#btOfferCount'),
    fill: $('#btOfferFill'),
    message: $('#btOfferMessage'),
    cta: $('#btOfferCta')
  } : null;
  const offerState = OFFER ? {
    products: Array.isArray(OFFER.products) ? OFFER.products.filter(p => p && p.id) : [],
    selections: {},
    added: {},
    loading: null,
    qualifyingIds: new Set((OFFER.qualifyingIds || []).map(Number).filter(Boolean)),
    minQty: Math.max(1, Number(OFFER.minQty) || 1),
    code: String(OFFER.code || '').trim(),
    unlockedTracked: false
  } : null;

  function offerProduct(handle) { return offerState?.products.find(p => p.handle === handle) || null; }
  function offerSelection(product) {
    if (!offerState.selections[product.handle]) offerState.selections[product.handle] = makeSelection(product);
    return offerState.selections[product.handle];
  }
  function offerCheckoutUrl(unlocked) {
    if (unlocked && OFFER.checkoutUrl) return OFFER.checkoutUrl;
    if (unlocked && offerState.code) return `/discount/${encodeURIComponent(offerState.code)}?redirect=/checkout`;
    return '/checkout';
  }
  function qualifyingCount(cart = state.cart) {
    if (!cart?.items?.length) return 0;
    return cart.items.reduce((total, item) => total + (offerState.qualifyingIds.has(Number(item.product_id)) ? Number(item.quantity || 0) : 0), 0);
  }
  function offerStatus() {
    const count = qualifyingCount();
    const need = offerState.minQty;
    const remaining = Math.max(0, need - count);
    const unlocked = count >= need;
    const value = OFFER.value || 'the offer';
    const noun = remaining === 1 ? 'shirt' : 'shirts';
    let message;
    if (unlocked) message = need > 1 ? `${value} unlocked. ${offerState.code} is applied at checkout.` : `${offerState.code} is applied at checkout.`;
    else if (count === 0) message = need > 1 ? `Add any ${need} qualifying shirts to unlock ${value}.` : `Add a shirt to use ${offerState.code} for ${value}.`;
    else message = `Add ${remaining} more qualifying ${noun} to unlock ${value}.`;
    return { count, need, remaining, unlocked, message };
  }

  function renderOfferTracker() {
    if (!offerEls?.tracker) return;
    const s = offerStatus();
    offerEls.tracker.classList.toggle('is-unlocked', s.unlocked);
    offerEls.tracker.classList.toggle('is-started', s.count > 0);
    if (offerEls.count) offerEls.count.textContent = s.need > 1 ? `${Math.min(s.count, s.need)} of ${s.need} added` : (s.count ? `${s.count} in cart` : 'Cart is empty');
    if (offerEls.fill) offerEls.fill.style.width = `${Math.min(100, (s.count / s.need) * 100)}%`;
    if (offerEls.message) offerEls.message.textContent = s.message;
    if (offerEls.cta) {
      if (s.unlocked) {
        offerEls.cta.textContent = `Checkout · ${offerState.code} applied`;
        offerEls.cta.setAttribute('href', offerCheckoutUrl(true));
        offerEls.cta.removeAttribute('data-scroll-buy');
      } else {
        offerEls.cta.textContent = s.count > 0 ? `Add ${s.remaining} more` : 'Choose your shirts';
        offerEls.cta.setAttribute('href', '#btBuy');
        offerEls.cta.setAttribute('data-scroll-buy', '');
      }
    }
    if (s.unlocked && !offerState.unlockedTracked) {
      offerState.unlockedTracked = true;
      track('offer_unlocked', { promotion_name: offerState.code, items: [] });
    }
  }

  function renderOfferCard(product) {
    const form = document.querySelector(`[data-card-form="${CSS.escape(product.handle)}"]`);
    if (!form) return;
    const selection = offerSelection(product);
    const variant = chosenVariant(product, selection);
    const visual = variant || displayVariant(product, selection);
    const sizeReady = hasSize(product, selection);
    const available = Boolean(variant?.id && variant.available !== false);
    const price = currentPrice(product, selection);
    const loading = offerState.loading === product.handle;
    const added = Boolean(offerState.added[product.handle]);
    const label = loading ? 'Adding…' : !sizeReady ? 'Choose a size' : !available ? 'Selection sold out' : added ? `Added · add another · ${money(price)}` : `Add to cart · ${money(price)}`;
    const groups = optionNames(product).map((_, i) => optionGroupHtml(product, selection, i, `card:${product.handle}`)).join('');
    form.innerHTML = `<div class="bt-options">${groups}</div><div class="bt-offer-card__action"><button class="bt-primary${added ? ' is-added' : ''}" type="button" data-card-add="${escapeAttr(product.handle)}"${loading || !sizeReady || !available ? ' disabled' : ''}>${escapeHtml(label)}</button><span class="bt-availability${sizeReady && !available ? ' is-error' : ''}">${sizeReady && !available ? 'Choose another option' : sizeReady ? 'In stock and ready to order' : 'Pick a size to add it'}</span></div>`;
    const priceEl = document.querySelector(`[data-card-price="${CSS.escape(product.handle)}"]`);
    if (priceEl) priceEl.textContent = money(price);
    const image = imageSource(visual?.featured_image);
    const imgEl = document.querySelector(`[data-card-image="${CSS.escape(product.handle)}"]`);
    if (image && imgEl && imgEl.src !== image) imgEl.src = image;
    const cardEl = document.querySelector(`[data-offer-card="${CSS.escape(product.handle)}"]`);
    if (cardEl) cardEl.classList.toggle('is-added', added);
  }

  function renderOfferCards() { offerState.products.forEach(renderOfferCard); }

  function selectCard(handle, index, value) {
    const product = offerProduct(handle);
    if (!product) return;
    const selection = offerSelection(product);
    selection.selected[index] = value;
    normalizeAfterChange(product, selection, index);
    renderOfferCard(product);
  }

  async function addCard(handle) {
    const product = offerProduct(handle);
    if (!product || offerState.loading) return;
    const selection = offerSelection(product);
    const sizeIndex = optionIndex(product, /size/i);
    if (sizeIndex >= 0 && !selection.selected[sizeIndex]) { showToast('Choose a size first.'); return; }
    const variant = chosenVariant(product, selection);
    if (!variant?.id || variant.available === false) { showToast('Choose an available option.'); return; }
    offerState.loading = handle; renderOfferCard(product);
    try {
      await addVariant(variant.id, 1);
      offerState.added[handle] = (offerState.added[handle] || 0) + 1;
      track('add_to_cart', { currency: 'USD', value: Number(variant.price || 0) / 100, items: [{ item_id: String(variant.id), item_name: product.title, item_variant: variant.title, price: Number(variant.price || 0) / 100, quantity: 1 }] });
      document.dispatchEvent(new CustomEvent('bt-lander:added', { detail: { product, variant, quantity: 1, source: 'offer-card' } }));
      await fetchCart();
      const s = offerStatus();
      if (s.unlocked || s.need === 1) openCart();
      else { showToast(`Added. ${s.message}`); renderOfferTracker(); }
    } catch { showToast('That item could not be added. Please try again.'); }
    finally { offerState.loading = null; renderOfferCard(product); }
  }

  async function copyOfferCode(trigger) {
    const code = offerState?.code;
    if (!code) return;
    try { await navigator.clipboard.writeText(code); }
    catch {
      const field = document.createElement('textarea'); field.value = code; field.setAttribute('readonly', ''); field.style.position = 'fixed'; field.style.opacity = '0';
      document.body.appendChild(field); field.select(); try { document.execCommand('copy'); } catch {} field.remove();
    }
    if (trigger) { const original = trigger.dataset.copyLabel || trigger.textContent; trigger.textContent = 'Copied'; trigger.classList.add('is-copied'); setTimeout(() => { trigger.textContent = original; trigger.classList.remove('is-copied'); }, 1800); }
    showToast(`${code} copied. It also applies automatically at checkout from this page.`);
    track('select_promotion', { promotion_name: code });
  }

  async function loadQualifying() {
    if (!OFFER.qualifyingEndpoint) return;
    try {
      const response = await fetch(OFFER.qualifyingEndpoint, { credentials: 'include', headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('qualifying_failed');
      const payload = await response.json();
      if (Array.isArray(payload.qualifyingProductIds) && payload.qualifyingProductIds.length) {
        payload.qualifyingProductIds.map(Number).filter(Boolean).forEach(id => offerState.qualifyingIds.add(id));
        renderOfferTracker();
        if (els.cartDrawer && !els.cartDrawer.hidden) renderCart();
      }
    } catch (error) { console.info('[bt-lander] Using featured products as the qualifying set:', error.message); }
  }

  function primeDiscountCookie() {
    if (!offerState.code || OFFER.silentApply === false) return;
    // Shopify sets the discount cookie on this response; checkout links still carry the code explicitly.
    try { fetch(`/discount/${encodeURIComponent(offerState.code)}?redirect=/`, { credentials: 'include', redirect: 'manual' }).catch(() => {}); } catch {}
  }

  function offerCartSummary() {
    if (!OFFER) return '';
    const s = offerStatus();
    const ctaHref = offerCheckoutUrl(s.unlocked);
    const cta = s.unlocked
      ? `<a href="${escapeAttr(ctaHref)}" class="is-unlocked">Checkout with ${escapeHtml(offerState.code)} · ${escapeHtml(OFFER.value || 'offer applied')}</a>`
      : `<a href="${escapeAttr(ctaHref)}">Checkout</a>`;
    return { progress: `<div class="bt-offer-progress${s.unlocked ? ' is-unlocked' : ''}"><div><span>${escapeHtml(offerState.code)}</span><strong>${s.unlocked ? 'Unlocked' : `${Math.min(s.count, s.need)} / ${s.need}`}</strong></div><div class="bt-shipping-progress__track"><span style="width:${Math.min(100, (s.count / s.need) * 100)}%"></span></div><p>${escapeHtml(s.message)}</p></div>`, cta, status: s };
  }

  if (OFFER) {
    document.addEventListener('bt-lander:cart', renderOfferTracker);
    document.addEventListener('click', event => {
      const cardOption = event.target.closest('[data-card-option]');
      if (cardOption) { selectCard(cardOption.dataset.card, Number(cardOption.dataset.cardOption), cardOption.dataset.value); return; }
      const cardAdd = event.target.closest('[data-card-add]');
      if (cardAdd) { addCard(cardAdd.dataset.cardAdd); return; }
      const copy = event.target.closest('[data-copy-code]');
      if (copy) { copyOfferCode(copy); return; }
      const jump = event.target.closest('[data-jump-card]');
      if (jump) { document.querySelector(`[data-offer-card="${CSS.escape(jump.dataset.jumpCard)}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }
    });
    document.addEventListener('change', event => {
      if (event.target.matches('[data-card-select]')) selectCard(event.target.dataset.card, Number(event.target.dataset.cardSelect), event.target.value);
    });
  }

  /* ---------- events ---------- */
  document.addEventListener('click',event => {
    const heroOption = event.target.closest('[data-hero-option]');
    if (heroOption) { selectHero(Number(heroOption.dataset.heroOption),heroOption.dataset.value); return; }
    const quickOption = event.target.closest('[data-quick-option]');
    if (quickOption) { selectQuick(Number(quickOption.dataset.quickOption),quickOption.dataset.value); return; }
    const quickButton = event.target.closest('[data-quick-shop]');
    if (quickButton) {
      const handle = quickButton.dataset.quickShop;
      const product = productByHandle(handle);
      if (product) openQuick(product);
      else fetchProduct(handle).then(openQuick).catch(() => showToast('That product could not be loaded.'));
      return;
    }
    const cartChange = event.target.closest('[data-cart-line][data-cart-qty]');
    if (cartChange) { changeCart(cartChange.dataset.cartLine,cartChange.dataset.cartQty); return; }
    if (event.target.closest('[data-cart-more]')) { closeCart(); (els.more || els.recRail || els.buy)?.scrollIntoView({behavior:'smooth'}); return; }
    if (event.target.closest('[data-cart-shop]')) { closeCart(); els.buy?.scrollIntoView({behavior:'smooth'}); return; }
    if (event.target.closest('[data-scroll-buy]')) { els.buy?.scrollIntoView({behavior:'smooth'}); return; }
    if (event.target.closest('[data-open-cart]')) { openCart(); return; }
    const thumb = event.target.closest('.bt-thumb');
    if (thumb) { setMainImage(thumb.dataset.gallerySrc, thumb.dataset.galleryAlt || heroProduct?.title || ''); return; }
    const chartButton = event.target.closest('[data-size-chart]');
    if (chartButton) { renderSizeGuide(chartButton.dataset.sizeChart); return; }
    const openButton = event.target.closest('[data-bt-open-modal]');
    if (openButton) { openModal(document.getElementById(openButton.dataset.btOpenModal)); return; }
    const closeButton = event.target.closest('[data-close-modal]');
    if (closeButton) { const modal=document.getElementById(closeButton.dataset.closeModal); if(modal) closeModal(modal); }
  });

  document.addEventListener('change',event => {
    if (event.target.matches('[data-hero-select]')) selectHero(Number(event.target.dataset.heroSelect),event.target.value);
    if (event.target.matches('[data-quick-select]')) selectQuick(Number(event.target.dataset.quickSelect),event.target.value);
  });

  $('#btQtyMinus')?.addEventListener('click',()=>{ state.quantity=Math.max(1,state.quantity-1); if (els.qtyOutput) els.qtyOutput.textContent=String(state.quantity); heroRender(); });
  $('#btQtyPlus')?.addEventListener('click',()=>{ state.quantity=Math.min(20,state.quantity+1); if (els.qtyOutput) els.qtyOutput.textContent=String(state.quantity); heroRender(); });
  els.add?.addEventListener('click',addHero);
  els.mobileAction?.addEventListener('click',()=>{ if(heroProduct && state.hero && hasSize(heroProduct,state.hero)) addHero(); else { const sizeIndex=optionIndex(heroProduct,/size/i); (document.querySelector(`[data-option-group="${sizeIndex}"]`) || els.buy)?.scrollIntoView({behavior:'smooth',block:'center'}); } });
  els.headerCart?.addEventListener('click',event=>{ event.preventDefault(); openCart(); });
  els.cartClose?.addEventListener('click',closeCart);
  els.cartOverlay?.addEventListener('click',closeCart);
  els.quickClose?.addEventListener('click',closeQuick);
  els.quickOverlay?.addEventListener('click',closeQuick);
  els.quickBody?.addEventListener('click',event=>{ if(event.target.closest('#btQuickAdd')) addQuick(); });
  $('#btSizeGuideOpen')?.addEventListener('click',openSizeGuide);
  $('#btZoomOpen')?.addEventListener('click',()=>openModal(els.imageModal));
  $$('.bt-modal').forEach(modal => modal.addEventListener('click',event=>{ if(event.target===modal) closeModal(modal); }));
  $('#btRecPrev')?.addEventListener('click',()=>moveRecs(-1));
  $('#btRecNext')?.addEventListener('click',()=>moveRecs(1));
  els.recRail?.addEventListener('scroll',()=>requestAnimationFrame(updateRecIndex),{passive:true});
  document.addEventListener('keydown',trapEscape);

  /* ---------- init ---------- */
  function startHero() {
    if (!heroProduct?.id) return;
    state.hero = makeSelection(heroProduct);
    buildGallery();
    heroRender();
    track('view_item',{ currency:'USD',value:Number(heroProduct.price_min || heroProduct.price || 0)/100,items:[{item_id:String(heroProduct.id),item_name:heroProduct.title,price:Number(heroProduct.price_min || heroProduct.price || 0)/100}] });
  }

  async function init() {
    resolveAssets();
    if (!heroProduct?.id && CONFIG.productHandle) {
      try {
        const response = await fetch(`/products/${CONFIG.productHandle}.js`, { credentials:'include', headers:{ Accept:'application/json' } });
        if (response.ok) heroProduct = await response.json();
      } catch (error) { console.warn('[bt-lander] Hero product could not be loaded', error); }
    }
    startHero();
    if (OFFER) {
      offerState.products.forEach(p => offerState.qualifyingIds.add(Number(p.id)));
      renderOfferCards();
      renderOfferTracker();
      if (offerEls.tracker && els.buy && 'IntersectionObserver' in window) {
        // Desktop: keep the floating tracker out of the hero until the shop section is reached.
        const reveal = new IntersectionObserver(entries => { if (entries.some(e => e.isIntersecting)) { offerEls.tracker.classList.add('is-visible'); reveal.disconnect(); } }, { rootMargin: '0px 0px -30% 0px' });
        reveal.observe(els.buy);
      } else offerEls?.tracker?.classList.add('is-visible');
      primeDiscountCookie();
      loadQualifying();
      track('view_promotion', { promotion_name: offerState.code, items: offerState.products.map((p, i) => ({ item_id: String(p.id), item_name: p.title, index: i + 1, price: Number(p.price_min || p.price || 0) / 100 })) });
    }
    fetchCart().catch(()=>{});
    loadRecs();
    enableDragScroll(els.recRail);
    setupMobileBar();
    document.dispatchEvent(new CustomEvent('bt-lander:ready', { detail: { product: heroProduct, config: CONFIG } }));
  }

  window.BTLander = {
    get config() { return CONFIG; },
    get product() { return heroProduct; },
    get state() { return state; },
    money, track, showToast,
    addVariant, fetchCart, openCart, closeCart, changeCart,
    openQuick, closeQuick, productByHandle, fetchProduct,
    openModal, closeModal, openSizeGuide, setMainImage,
    heroRender, selectHero,
    get offer() { return OFFER ? { status: offerStatus(), state: offerState, renderTracker: renderOfferTracker, renderCards: renderOfferCards, copyCode: copyOfferCode } : null; }
  };

  init();
})();
